"use client";

import * as Dialog from "@radix-ui/react-dialog";
import dynamic from "next/dynamic";
import { Maximize2, X } from "lucide-react";
import type { RouteMapData } from "@/lib/dto";

const RouteMap = dynamic(
  () =>
    import("@/components/trip/RouteMap").then((m) => ({ default: m.RouteMap })),
  {
    ssr: false,
    loading: () => (
      <div className="flex h-full items-center justify-center text-muted">
        Loading map…
      </div>
    ),
  },
);

type Props = {
  routeMap?: RouteMapData | null;
  title: string;
};

/** Trigger button + full-screen popup for the interactive route map. */
export function RouteMapDialog({ routeMap, title }: Props) {
  const hasValidRoute =
    routeMap?.route &&
    routeMap.route.length > 0 &&
    routeMap.route.some((day) => day.stops.length > 0);

  if (!hasValidRoute) return null;

  return (
    <Dialog.Root>
      {/* Darkens the whole map image on hover, independent of the trigger
          button below — kept off the button itself so the button stays a
          fixed bottom-right pill instead of stretching to fill the card. */}
      <span
        aria-hidden
        className="absolute inset-0 z-[5] bg-black/10 opacity-0 transition-opacity group-hover:opacity-100"
      />
      <Dialog.Trigger
        type="button"
        className="absolute bottom-4 right-4 z-10 inline-flex items-center justify-center gap-x-1.5 rounded-md bg-primary px-4 py-3 text-center text-sm font-semibold text-white shadow-lg transition-colors hover:bg-primary/90"
      >
        <Maximize2 size={16} aria-hidden />
        View Detailed Map
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/70 data-[state=open]:animate-in data-[state=open]:fade-in" />
        <Dialog.Content className="fixed inset-2 z-50 overflow-hidden rounded-lg bg-white shadow-2xl sm:inset-6">
          <Dialog.Title className="sr-only">{title}</Dialog.Title>
          <Dialog.Description className="sr-only">
            Interactive day-by-day route map for {title}
          </Dialog.Description>
          <Dialog.Close
            aria-label="Close map"
            className="absolute right-3 top-3 z-20 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white text-muted-soft shadow-md hover:bg-bg"
          >
            <X size={18} aria-hidden />
          </Dialog.Close>
          <div className="h-full w-full">
            <RouteMap route={routeMap.route} className="h-full" />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
